import React from 'react';
import { IVerbaScholaExerciseChoice, IVerbaScholaExerciseConversation, IVerbaScholaLessonExerciseStep, IVerbaScholaSpeakingSentence } from '../api/VerbaScholaApi';
import VerbaScholaConversationEditor from './VerbaScholaConversationEditor';
import VerbaScholaQuizEditor from './VerbaScholaQuizEditor';
import VerbaScholaSpeakingEditor from './VerbaScholaSpeakingEditor';
import VerbaScholaTypedAnswerEditor from './VerbaScholaTypedAnswerEditor';
import { LessonReviewUnkeptContext } from './lessonReviewReadOnly';
import type { ReviewEditKind } from './proposalReviewModel';

/** An activity the page edits with its own editor: a question and its answers, speaking targets, accepted answers or a conversation script. */
export type OwnEditorActivity =
    | { kind: 'quiz'; choices: IVerbaScholaExerciseChoice[] }
    | { kind: 'speaking' | 'typed'; sentences: IVerbaScholaSpeakingSentence[] }
    | { kind: 'conversation'; conversation: IVerbaScholaExerciseConversation; lines: IVerbaScholaSpeakingSentence[] };

/**
 * The activity's own editor, when it has one, under the same conditions as the exercises page shows it: ``sentences``
 * are the step's own speaking rows, ``lines`` every sentence row of the lesson content (a conversation's lines belong
 * to its conversation, not the step).
 */
export function ownEditorActivity(step: IVerbaScholaLessonExerciseStep, choices: IVerbaScholaExerciseChoice[], sentences: IVerbaScholaSpeakingSentence[],
    conversations: IVerbaScholaExerciseConversation[], lines: IVerbaScholaSpeakingSentence[]): OwnEditorActivity | null {
    const chinese = (row: IVerbaScholaSpeakingSentence) => row.sentence_class_name === 'SentenceChineseModel';
    if (step.class_name === 'speak_with_grade' && sentences.length >= 1 && sentences.length <= 5 && !choices.length
        && sentences.every(row => chinese(row) && row.student_response === null && row.student_response_pinyin === null && row.speaker_name === null)) {
        return { kind: 'speaking', sentences };
    }
    if (step.class_name === 'type_speak' && sentences.length >= 1 && sentences.length <= 8 && !choices.length
        && sentences.every(row => chinese(row) && row.speaker_name === null && row.student_response === row.text && JSON.stringify(row.student_response_pinyin) === JSON.stringify(row.pinyin))) {
        return { kind: 'typed', sentences };
    }
    if (step.class_name === 'conversation_listen' && conversations.length === 1 && !choices.length && !sentences.length) {
        const own = lines.filter(row => row.verbaschola_lesson_exercise_step_conversation_id === conversations[0].id);
        if (own.length >= 2 && own.length <= 20 && own.every(row => chinese(row) && row.student_response === null && row.student_response_pinyin === null && Boolean(row.speaker_name))) {
            return { kind: 'conversation', conversation: conversations[0], lines: own };
        }
    }
    if (step.class_name === 'quiz_question' && step.check_content_json?.mode !== 'picture' && !step.check_content_json?.display_audio && !sentences.length
        && choices.length >= 2 && choices.length <= 8 && choices.every(choice => choice.sentence_id === null && choice.verbaschola_lesson_vocabulary_target_id === null
            && Boolean(choice.choice_text?.trim()) && !choice.choice_content_json?.image_src_url)) {
        return { kind: 'quiz', choices };
    }
    return null;
}

/** One row an activity editor's save changed, by the id shown in Your version. */
export interface ActivityRowEdit { kind: ReviewEditKind; id: number; fields: Record<string, unknown> }

// An empty value reads the same whether stored as null, an empty text or an empty list.
const blank = (value: unknown) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
const differs = (a: unknown, b: unknown) => !(blank(a) && blank(b)) && JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
function changed(row: object | undefined, values: Record<string, unknown>): Record<string, unknown> {
    const current = (row ?? {}) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(values).filter(([field, value]) => differs(current[field], value)));
}
/** A sentence's new text and pinyin, together: the save links the row to a sentence with both. */
function sentenceText(row: IVerbaScholaSpeakingSentence | undefined, text: string, pinyin: string[]): Record<string, unknown> {
    return differs(row?.text, text) || differs(row?.pinyin, pinyin) ? { sentence_text: text, sentence_pinyin: pinyin } : {};
}

/**
 * An activity's own editor in Your version under review: its save keeps the changed rows in the review draft
 * (``onKeep``), and the review's Save writes them with the AI changes. Adding, removing or reordering rows stays
 * with the saved lesson's editors.
 */
export default function VerbaScholaReviewActivityEditor({ step, activity, onKeep }: {
    step: IVerbaScholaLessonExerciseStep; activity: OwnEditorActivity; onKeep: (edits: ActivityRowEdit[]) => void;
}) {
    // Its own key, as each editor of the review has one: keeping or cancelling another editor leaves its text alone.
    const key = `review:specialized:${step.id}`;
    const mark = React.useContext(LessonReviewUnkeptContext);
    // Every change reports the open text again: the bar's Discard and continue forgets it while the editor stays open.
    const setUnkept = (unkept: boolean) => mark?.(key, unkept);
    React.useEffect(() => () => mark?.(key, false), [mark, key]);
    const keep = (edits: ActivityRowEdit[]) => {
        const kept = edits.filter(edit => Object.keys(edit.fields).length);
        if (kept.length) onKeep(kept);
        setUnkept(false);
        return Promise.resolve();
    };
    // The activity's own fields only: the editor's save also carries its rows and the lesson revision.
    const stepEdit = ({ title, prompt_text, prompt_pinyin }: { title: string; prompt_text: string; prompt_pinyin: string[] }): ActivityRowEdit =>
        ({ kind: 'exercise_step', id: step.id, fields: changed(step, { title, prompt_text, prompt_pinyin }) });
    const common = { step, revision: 0, disabled: false, onDirty: () => setUnkept(true), onCancel: () => setUnkept(false) };
    switch (activity.kind) {
    case 'quiz': return <VerbaScholaQuizEditor {...common} choices={activity.choices} onSave={(_id, payload) => keep([stepEdit(payload),
        ...payload.choices.map(choice => ({ kind: 'exercise_step_choice' as const, id: choice.id, fields: changed(activity.choices.find(row => row.id === choice.id),
            { choice_text: choice.choice_text, choice_pinyin: choice.choice_pinyin, is_correct: choice.is_correct }) }))])} />;
    case 'speaking': return <VerbaScholaSpeakingEditor {...common} sentences={activity.sentences} onSave={(_id, payload) => keep([stepEdit(payload),
        ...payload.sentences.map(row => ({ kind: 'exercise_step_sentence' as const, id: row.id,
            fields: sentenceText(activity.sentences.find(item => item.id === row.id), row.text, row.pinyin) }))])} />;
    case 'typed': return <VerbaScholaTypedAnswerEditor {...common} sentences={activity.sentences} onSave={(_id, payload) => keep([stepEdit(payload),
        ...payload.sentences.map(row => ({ kind: 'exercise_step_sentence' as const, id: row.id, fields: changed(activity.sentences.find(item => item.id === row.id),
            { student_response: row.student_response, student_response_pinyin: row.student_response_pinyin }) }))])} />;
    case 'conversation': return <VerbaScholaConversationEditor {...common} conversation={activity.conversation} sentences={activity.lines} onSave={(_id, payload) => keep([stepEdit(payload),
        { kind: 'exercise_step_conversation', id: activity.conversation.id, fields: changed(activity.conversation, { name: payload.conversation.name }) },
        ...payload.conversation.sentences.map(row => {
            const saved = activity.lines.find(item => item.id === row.id);
            return { kind: 'exercise_step_sentence' as const, id: row.id, fields: { ...changed(saved, { speaker_name: row.speaker_name }), ...sentenceText(saved, row.text, row.pinyin) } };
        })])} />;
    }
}
